import { LEGAL_DOCUMENT_PATHS, type LegalDocumentKind } from '@provision/landing-page-utils';
import type {
  LegalDocument,
  SaveLegalDocumentInput,
} from 'src/graphql/entities/legal-document/legal-document.types';

/** A segment uses one document of each kind, so taking one releases the other. */
export function assignInList(
  documents: LegalDocument[],
  {
    brandId,
    kind,
    legalDocumentId,
  }: { brandId: string; kind: LegalDocumentKind; legalDocumentId: string },
): LegalDocument[] {
  return documents.map((document) => {
    if (document.kind !== kind) return document;

    const others = document.brandIds.filter((id) => id !== brandId);

    return {
      ...document,
      brandIds: document.legalDocumentId === legalDocumentId ? [...others, brandId] : others,
    };
  });
}

/**
 * Stands in for a new document until the server answers with its real id. It only lives in the
 * cache and is never sent back.
 */
export const PENDING_DOCUMENT_ID = 'pending-new-document';

export function saveInList(
  documents: LegalDocument[],
  input: SaveLegalDocumentInput,
  now: string,
): LegalDocument[] {
  const fields = {
    name: input.name,
    title: input.title,
    bodyHtml: input.bodyHtml,
    updatedAt: now,
  };
  const legalDocumentId = input.legalDocumentId ?? PENDING_DOCUMENT_ID;

  const withDocument = input.legalDocumentId
    ? documents.map((document) =>
        document.legalDocumentId === input.legalDocumentId
          ? { ...document, ...fields }
          : document,
      )
    : [
        ...documents,
        {
          legalDocumentId,
          kind: input.kind,
          path: LEGAL_DOCUMENT_PATHS[input.kind],
          createdAt: now,
          brandIds: [],
          ...fields,
        },
      ];

  return assignInList(withDocument, { ...input, legalDocumentId });
}
